import cookie from 'cookie';
import type { IncomingMessage } from 'http';

import {
  checkAnalyticsConsent,
  checkAnalyticsConsentServerSide,
} from '@/lib/analyticsConsent';
import {
  AUTH_ACCESS_TOKEN_COOKIE,
  AUTH_REFRESH_TOKEN_COOKIE,
  getServerSideUserId,
  isAuthenticatedServerSide,
} from '@/lib/auth/cookieStorage';

export { checkAnalyticsConsent } from '@/lib/analyticsConsent';

// Cookie name for storing the stable user UUID
const ALGOLIA_USER_ID_COOKIE = 'algolia_user_id';

function extractGoogleAnalyticsUserIdFromCookie(
  gaCookie: string,
): string | undefined {
  if (gaCookie) {
    const userIdParts = gaCookie.split('.').slice(-2);
    if (userIdParts.length === 2) {
      return userIdParts.join('_');
    }
  }
  return undefined;
}

function sanitizeForAlgolia(input: string): string {
  let sanitized = input.replace(/[^a-zA-Z0-9_-]/g, '_');

  if (sanitized.length > 64) {
    sanitized = sanitized.substring(0, 64);
  }

  if (sanitized.length === 0) {
    sanitized = 'user';
  }

  return sanitized;
}

function getBrowserCookie(cookieName: string): string | undefined {
  if (typeof document !== 'object' || typeof document.cookie !== 'string') {
    return undefined;
  }

  const name = cookieName + '=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      const cookieValue = c.substring(name.length, c.length);
      return cookieValue;
    }
  }

  return undefined;
}

const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`; //NOSONAR just fallback uuid
};

/**
 * Set the user ID cookie (client-side).
 * Cookie is set for 1 year with SameSite=Lax for security.
 */
const setUserIdCookie = (uuid: string): void => {
  if (typeof document === 'undefined') return;

  try {
    const maxAge = 365 * 24 * 60 * 60; // 1 year in seconds
    document.cookie = `${ALGOLIA_USER_ID_COOKIE}=${uuid}; path=/; max-age=${maxAge}; SameSite=Lax`;
  } catch {
    // Cookie setting failed
  }
};

/**
 * Get or generate the stable user UUID from cookie (client-side).
 */
const getOrCreateUserId = (): string => {
  const existingId = getBrowserCookie(ALGOLIA_USER_ID_COOKIE);
  if (existingId) return existingId;

  const newId = generateUUID();
  setUserIdCookie(newId);
  return newId;
};

/**
 * Get the Google Analytics user token from _ga cookie (client-side).
 */
export const getGoogleAnalyticsUserToken = (): string | undefined => {
  const gaCookie = getBrowserCookie('_ga');
  if (gaCookie) {
    return extractGoogleAnalyticsUserIdFromCookie(gaCookie);
  }
  return undefined;
};

function decodeTokenPayloadClientSide(
  token: string,
): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = atob(base64);
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

function getClientSideUserId(): string | null {
  for (const cookieName of [
    AUTH_ACCESS_TOKEN_COOKIE,
    AUTH_REFRESH_TOKEN_COOKIE,
  ]) {
    const raw = getBrowserCookie(cookieName);
    if (raw) {
      const token = decodeURIComponent(raw);
      const userId = decodeTokenPayloadClientSide(token)?.user_id;
      if (typeof userId === 'string') return userId;
    }
  }
  return null;
}

/**
 * Build the Algolia user token for search API calls (client-side).
 *
 * Per BEC-20960, the token format depends on auth state:
 * - Authenticated: `user_<userId>` (read from auth cookies)
 * - Anonymous + consented: `anonymous_consented_<ga_id>`
 * - Anonymous + consented but no GA yet: `anonymous_consented_<uuid>`
 * - Anonymous + no consent: `anonymous_<uuid>`
 */
export const getSearchCallUserToken = (): string => {
  const refreshToken = getBrowserCookie(AUTH_REFRESH_TOKEN_COOKIE);
  if (refreshToken) {
    const userId = getClientSideUserId();
    if (userId && userId.trim() !== '') {
      return sanitizeForAlgolia(`user_${userId.trim()}`);
    }
  }
  return getUserTokenForAlgolia();
};

/**
 * Build the anonymous Algolia user token based on consent/GA state (client-side).
 *
 * Token format:
 * - Consented: `anonymous_consented_<ga_id>` (or `anonymous_consented_<uuid>` if no GA yet)
 * - Not consented: `anonymous_<uuid>`
 *
 * Used by analytics events where userToken must remain anonymous.
 * For search calls, use `getSearchCallUserToken()` instead.
 */
export const getUserTokenForAlgolia = (): string => {
  const uuid = getOrCreateUserId();
  const hasConsent = checkAnalyticsConsent();

  if (hasConsent) {
    const gaId = getGoogleAnalyticsUserToken();
    if (gaId) {
      // anonymous_consented_{google analytics id}
      return sanitizeForAlgolia(`anonymous_consented_${gaId}`);
    }
    // Consented but no GA yet - use UUID with "consented" marker (temporary)
    return sanitizeForAlgolia(`anonymous_consented_${uuid}`);
  }

  // No consent - use UUID without "consented" marker
  return sanitizeForAlgolia(`anonymous_${uuid}`);
};

/**
 * Get the authenticated user token for Algolia analytics events.
 *
 * Per BEC-20960 and Algolia documentation:
 * - authenticatedUserToken: Only sent when user is authenticated
 * - Format: `user_<userId>`
 *
 * @returns The authenticated user token, or undefined if not authenticated
 */
export const getAuthenticatedUserToken = (
  isAuthenticated: boolean | null | undefined,
  userId?: string | null,
): string | undefined => {
  const isLoggedIn =
    isAuthenticated === true && !!userId && userId.trim() !== '';

  if (!isLoggedIn) {
    return undefined;
  }

  return sanitizeForAlgolia(`user_${userId!.trim()}`);
};

export const getTokenStatus = (
  isAuthenticated: boolean | null | undefined,
  userId?: string | null,
) => {
  return {
    isAuthenticated,
    userId,
    gaToken: getGoogleAnalyticsUserToken(),
    currentToken: getUserTokenForAlgolia(),
  };
};

// =============================================================================
// Server-Side Utilities (for SSR/getServerSideProps)
// =============================================================================

/**
 * Get the stable user UUID from request cookies (server-side).
 */
const getServerSideUserId_Algolia = (
  cookies: Record<string, string>,
): string | null => {
  return cookies[ALGOLIA_USER_ID_COOKIE] ?? null;
};

/**
 * Get Google Analytics user token from server-side cookies.
 */
const getGoogleAnalyticsUserTokenServerSide = (
  cookies: Record<string, string>,
): string | undefined => {
  const gaCookie = cookies['_ga'];
  if (gaCookie) {
    const userIdParts = gaCookie.split('.').slice(-2);
    if (userIdParts.length === 2) {
      return userIdParts.join('_');
    }
  }
  return undefined;
};

/**
 * Build the Algolia user token for server-side rendering.
 *
 * Per BEC-20960, for search calls the token format depends on auth state:
 * - Authenticated: `user_<userId>`
 * - Anonymous + consented: `anonymous_consented_<ga_id>`
 * - Anonymous + consented but no GA yet: `anonymous_consented_<uuid>`
 * - Anonymous + not consented: `anonymous_<uuid>`
 *
 * @param req - The incoming HTTP request (from getServerSideProps context)
 * @returns The Algolia user token, or undefined if cookies are not available
 */
export const getUserTokenForAlgoliaSSR = (
  req?: IncomingMessage,
): string | undefined => {
  if (!req) {
    return undefined;
  }

  // BEC-20960: Authenticated users get `user_{userId}` for search calls
  const isAuthenticated = isAuthenticatedServerSide(req);
  if (isAuthenticated) {
    const userId = getServerSideUserId(req);
    if (userId && userId.trim() !== '') {
      return sanitizeForAlgolia(`user_${userId.trim()}`);
    }
  }

  const cookieHeader = req.headers?.cookie;
  if (!cookieHeader) {
    return undefined;
  }

  const cookies = cookie.parse(cookieHeader);

  const uuid = getServerSideUserId_Algolia(cookies);
  if (!uuid) {
    return undefined;
  }

  const hasConsent = checkAnalyticsConsentServerSide(cookies);

  if (hasConsent) {
    const gaId = getGoogleAnalyticsUserTokenServerSide(cookies);
    if (gaId) {
      return sanitizeForAlgolia(`anonymous_consented_${gaId}`);
    }
    return sanitizeForAlgolia(`anonymous_consented_${uuid}`);
  }

  return sanitizeForAlgolia(`anonymous_${uuid}`);
};

/**
 * Get the authenticated user token for SSR.
 *
 * Per BEC-20960:
 * - authenticatedUserToken: Only sent when user is authenticated
 * - Format: `user_<userId>`
 *
 * @param req - The incoming HTTP request
 * @returns The authenticated user token, or undefined if not authenticated
 */
export const getAuthenticatedUserTokenSSR = (
  req: IncomingMessage,
): string | undefined => {
  const isAuthenticated = isAuthenticatedServerSide(req);
  const userId = isAuthenticated ? getServerSideUserId(req) : null;
  const isLoggedIn = isAuthenticated && !!userId && userId.trim() !== '';

  if (!isLoggedIn) {
    return undefined;
  }

  return sanitizeForAlgolia(`user_${userId!.trim()}`);
};

/**
 * Get authentication state for SSR.
 *
 * Returns auth info that can be passed to client-side components
 * to avoid hydration mismatches.
 *
 * @param req - The incoming HTTP request
 * @returns Object containing isAuthenticated and userId
 */
export const getAuthStateForSSR = (
  req: IncomingMessage,
): { isAuthenticated: boolean; userId: string | null } => {
  const isAuthenticated = isAuthenticatedServerSide(req);
  const userId = isAuthenticated ? getServerSideUserId(req) : null;

  return {
    isAuthenticated,
    userId,
  };
};
